import React, { useState } from 'react';
import { getService } from '@Shared.Angular/flowingly.hooks/useService';
import IFlowinglySearchResult from '@Shared.Angular/@types/core/contracts/queryModel/search/flowinglySearchResult';
import { SearchEntityType } from '@Shared.Angular/flowingly.services/flowingly.constants';
import { EmptyGuid } from '@Shared.Angular/@types/guid';
import { TextButton } from '@Shared.Angular/flowingly.components/buttons/TextButton';

interface Props {
  selectedEmails: IFlowinglySearchResult[];
  onEmailAdd: (email: IFlowinglySearchResult) => void;
}

const CustomEmailInput: React.FC<Props> = ({ selectedEmails, onEmailAdd }) => {
  const userApiService = getService('userApiService');
  const validationService = getService('validationService');

  const [emailInputValue, setEmailInputValue] = useState('');
  const [emailInputError, setEmailInputError] = useState('');
  const [isAddingEmail, setIsAddingEmail] = useState(false);

  // Email validation pattern from validationService
  const emailValidationPattern = validationService.getEmailValidationPattern();

  const validateEmail = (email: string): boolean => {
    return emailValidationPattern.test(email.trim());
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && emailInputValue.trim()) {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleEmailInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailInputValue(e.target.value);
    if (emailInputError) setEmailInputError('');
  };

  const handleAddEmail = async () => {
    if (isAddingEmail) return;

    const email = emailInputValue.trim();

    if (selectedEmails.some((item) => item.displayName === email)) {
      setEmailInputValue('');
      setEmailInputError('');
      return;
    }

    setIsAddingEmail(true);
    setEmailInputError('');

    try {
      // API Validation - Check if valid external user
      const response = await userApiService.isValidExternalUser(email);

      if (response && response.isValid) {
        const newEmailItem: IFlowinglySearchResult = {
          id: EmptyGuid,
          displayName: email,
          searchEntityType: SearchEntityType.CUSTOM_EMAIL
        };

        onEmailAdd(newEmailItem);
        setEmailInputValue('');
        setEmailInputError('');
      } else {
        setEmailInputError(
          'This email is associated with an unlicensed business user.'
        );
      }
    } catch (error) {
      console.error('Error validating external user:', error);
      setEmailInputError('Failed to validate email. Please try again.');
    } finally {
      setIsAddingEmail(false);
    }
  };

  return (
    <div className="custom-email-input-container">
      <div className="email-input-row">
        <input
          id="custom-email-input"
          name="customEmailInput"
          type="email"
          className={`custom-email-input text-box ${
            emailInputError ? 'error' : ''
          }`}
          value={emailInputValue}
          onChange={handleEmailInputChange}
          onKeyDown={handleEmailKeyDown}
          placeholder="Enter email address"
          required
        />
        <TextButton
          id="custom-email-add-button"
          text={isAddingEmail ? 'Adding...' : 'Add'}
          type="secondary"
          onClick={handleAddEmail}
          disabled={
            !emailInputValue.trim() ||
            !validateEmail(emailInputValue) ||
            isAddingEmail
          }
        />
      </div>
      <div className="email-error-messages-container">
        {emailInputError && emailInputValue.length > 0 && (
          <div className="email-input-error">{emailInputError}</div>
        )}
      </div>
    </div>
  );
};

export default CustomEmailInput;
